import { Location } from '../../commonInfra/utils/Location';
import { getSessionManagementServiceInstance, SessionManagementService } from '../services/SessionManagementService';

import { BindUI } from '../common/UIBinding';
import { QuerySuggestionTestId } from '../components/QuerySuggestion';
import { OmniBoxItemType } from '../models/Types';
import { IKeyboardNavigableItem, QuickSearchModule } from './QuickSearchModule';
import { FeatureFlag, FeatureFlagService } from '../services/FeatureFlagService';
import { searchTelemetry } from '../components/SearchTelemetry';
import { SearchEPTService } from '../services/SearchEPTService';
import { EPTSessionType } from '@tableau/microview-communication-service/lib/src/channelDefinitions/SearchEPTChannelDefinitions';
import { OmniboxConfigurationService } from '../OmniboxConfigurationService';

export const ImpressionType = 'querySuggestion';
export const LocalStoraygeKeyPrefix = 'pastQueryItems';

export type PastQueryItem = {
    searchTerm: string;
    hitCount: number;
    timestamps: number[];
    score?: number;
}

export interface QuerySuggestionItem extends IKeyboardNavigableItem {
    url: string;
    searchTerm: string;
    segments: { text: string, keywordToAppend: boolean }[];
    isPastQuery: boolean;
}

export type PastQueryStorage = {
    data: { [searchTerm: string]: PastQueryItem };
};

const hour = 1000 * 60 * 60;
const recentScoreHandlers: ((milliseconds: number) => number)[] = [
    (milliseconds) => { if (milliseconds <= 4 * hour) return 100; }, // within 4 hours
    (milliseconds) => { if (milliseconds <= 24 * hour) return 80; }, // within 24 hours
    (milliseconds) => { if (milliseconds <= 3 * 24 * hour) return 60; }, // within 3 days
    (milliseconds) => { if (milliseconds <= 7 * 24 * hour) return 40; }, // within 1 week
    (milliseconds) => { if (milliseconds <= 30 * 24 * hour) return 20; }, // within 1 month
    (milliseconds) => { if (milliseconds <= 90 * 24 * hour) return 10; }, // within 3 months
    (milliseconds) => { if (milliseconds > 90 * 24 * hour) return 1; } // older than 3 months
];
const maxTimestampsForEachPastQueryItem = 10;
const maxPastQueryitemsInStorage = 100;

export class QuerySuggestionViewModel extends QuickSearchModule {
    private server: SessionManagementService;

    @BindUI() public clearConfirmationDialogIsOpen: boolean;

    public clearButtonNavigationItem: IKeyboardNavigableItem = { ownerModule: this, getIdentifier: () => QuerySuggestionTestId + '-Clear' };
    public pastQueryStorage: PastQueryStorage;
    @BindUI(QuickSearchModule.buildKeyboardNavigationPath) public querySuggestionItems: QuerySuggestionItem[];
    @BindUI() public shouldShowHeader = true;
    @BindUI() public currentKeyboardNavigableItem?: IKeyboardNavigableItem;

    public initialize(): void {
        this.server = getSessionManagementServiceInstance();

        const initialPastQueryStorageData: PastQueryStorage = {
            data: {}
        };

        try {
            this.pastQueryStorage = JSON.parse(localStorage.getItem(this.getLocalStorageKey())) || initialPastQueryStorageData;
        } catch {
            this.pastQueryStorage = initialPastQueryStorageData;
        }

        if (!this.pastQueryStorage.data) {
            // The previous version of past query storage uses array. The latest query storage uses "data" property".
            // If it doesn't have "data" property, it means it's not the latest data. Then we assign the initial data to it.
            this.pastQueryStorage = initialPastQueryStorageData;
        }
    }

    public selectItemByKeyboard(): void {
        const currentItem = this.currentKeyboardNavigableItem as QuerySuggestionItem;
        if (currentItem.url) {
            // If it has url, it means it's a query suggestion item.
            this.hitPastQueryItem(currentItem, this.querySuggestionItems.indexOf(currentItem));
        } else {
            // Otherwise it's the "Clear" button.
            this.onClearClicked();
        }
    }

    public isKeyboardNavigable() {
        return !this.isUnmounted && this.querySuggestionItems && this.querySuggestionItems.length > 0;
    }

    public buildKeyboardNavigationPathInOwnModule() {
        if (this.isKeyboardNavigable()) {
            this.getFirstKeyboardNavigableItem().up = this.previousKeyboardNavigableModule.getLastKeyboardNavigableItem();

            for (let i = 0; i < this.querySuggestionItems.length - 1; i++) {
                const currentItem = this.querySuggestionItems[i];
                const nextItem = this.querySuggestionItems[i + 1];
                this.linkTwoKeyboardNavigableItems(currentItem, nextItem, true);
            }

            if (this.shouldShowHeader) {
                // If the header is shown, set the "Clear" button to be the next keyboard navigable item of the last query suggestion item.
                this.linkTwoKeyboardNavigableItems(this.querySuggestionItems[this.querySuggestionItems.length - 1], this.clearButtonNavigationItem, true);
            }

            this.getLastKeyboardNavigableItem().down = this.nextKeyboardNavigableModule.getFirstKeyboardNavigableItem();
        }
    }

    public getFirstKeyboardNavigableItem(): IKeyboardNavigableItem {
        return this.querySuggestionItems[0];
    }

    public getLastKeyboardNavigableItem(): IKeyboardNavigableItem {
        // If the header is shown, set the "Clear" button to be the last keyboard navigable item of this module.
        // Otherwise set the last query suggestion item to be the last keyboard navigable item of this module.
        return this.shouldShowHeader ? this.clearButtonNavigationItem : this.querySuggestionItems[this.querySuggestionItems.length - 1];
    }

    public generateQuerySuggestionItems() {
        const searchTerm = this.omniboxViewModel.searchTerm.toLowerCase().trim();

        const newQuerySuggestionItems = this.getQuerySuggestionedItemsFromPastQueryStorage(searchTerm);

        if (searchTerm) {
            newQuerySuggestionItems.unshift({
                url: this.omniboxViewModel.getUrlBySearchTerm(this.omniboxViewModel.searchTerm),
                searchTerm: this.omniboxViewModel.searchTerm,
                segments: [{ text: this.omniboxViewModel.searchTerm, keywordToAppend: false }],
                isPastQuery: false,
                ownerModule: this,
                getIdentifier: () => QuerySuggestionTestId + '-SearchTerm'
            });
        }

        this.shouldShowHeader = !searchTerm;

        this.querySuggestionItems = newQuerySuggestionItems.slice();

        setTimeout(() => {
            if (!this.isUnmounted && FeatureFlagService.instance.getValue(FeatureFlag.searchProductMetrics) && this.querySuggestionItems.length > 0) {
                searchTelemetry.sendSearchImpressionEvent(searchTerm, ImpressionType);
            }
        });
    }

    public hitPastQueryItem(querySuggestionItem: QuerySuggestionItem, index: number) {
        if (FeatureFlagService.instance.getValue(FeatureFlag.searchEPT)) {
            SearchEPTService.instance.start(EPTSessionType.fullSearch, querySuggestionItem.searchTerm);
        }

        this.hitPastQueryItemBySearchTerm(querySuggestionItem.searchTerm);
        // When a past query item is hit, the UI just redirects to search result page with the related search term, so we should keep the current search flow.
        // The when the following function is called, the "skipClearSearchFlowId" parameter is set to true.
        this.omniboxViewModel.onResultItemSelected(querySuggestionItem.isPastQuery ? OmniBoxItemType.pastQueryItem : OmniBoxItemType.searchTermItem, this.querySuggestionItems.length, index, '-1', true, true);

        if (!this.omniboxViewModel.inSearchResultPage) {
            Location.instance.setUrl(querySuggestionItem.url);
        } else {
            if (this.omniboxViewModel.onSearchTermChanged) {
                this.omniboxViewModel.onSearchTermChanged(querySuggestionItem.searchTerm);
            }
        }
    }

    public hitPastQueryItemBySearchTerm(searchTerm: string) {
        const actualSearchText = searchTerm.trim().toLowerCase();
        if (actualSearchText) {
            this.omniboxViewModel.searchTerm = actualSearchText;

            const existingPastQueryItem = this.pastQueryStorage.data[actualSearchText];
            const now = this.getNowTicks();
            if (existingPastQueryItem) {
                existingPastQueryItem.hitCount++;
                existingPastQueryItem.timestamps.unshift(now);
                existingPastQueryItem.timestamps = existingPastQueryItem.timestamps.slice(0, maxTimestampsForEachPastQueryItem);
            } else {
                const newPastQueryItem: PastQueryItem = {
                    searchTerm: actualSearchText,
                    hitCount: 1,
                    timestamps: [now],
                };
                this.pastQueryStorage.data[actualSearchText] = newPastQueryItem;
            }
            this.flushLocalStorage();
        }
    }

    public onClearClicked() {
        this.clearConfirmationDialogIsOpen = true;
    }

    public onClearConformationDialogDismissed() {
        this.clearConfirmationDialogIsOpen = false;
        this.omniboxViewModel.focusOnSearchBox();
    }

    public removeAllpastQueryItem() {
        this.clearConfirmationDialogIsOpen = false;
        this.omniboxViewModel.focusOnSearchBox();
        this.pastQueryStorage.data = {};
        this.flushLocalStorage();
        this.generateQuerySuggestionItems();
    }

    public getNowTicks() {
        return (new Date()).getTime();
    }

    private flushLocalStorage() {
        this.truncateStorageToFollowMaxCapacity();
        localStorage.setItem(this.getLocalStorageKey(), JSON.stringify(this.pastQueryStorage));
    }

    private truncateStorageToFollowMaxCapacity() {
        // This method ensure the past query items in the storage won't excceed the maxPastQueryitemsInStorage.
        // It firstly sort all the past query items by theirs scores in desc order.
        // Then only keep the top score items basd on maxPastQueryitemsInStorage.

        Object.keys(this.pastQueryStorage.data).forEach(key => {
            this.calculatePastQueryItemScore(this.pastQueryStorage.data[key]);
        });

        const pastQueryItems = Object.keys(this.pastQueryStorage.data).reduce<PastQueryItem[]>((p, c) => {
            p.push(this.pastQueryStorage.data[c]);
            return p;
        }, []);
        const finalPastQueryItems = pastQueryItems.sort((a, b) => this.comparePastQueryItemByScore(a, b)).slice(0, maxPastQueryitemsInStorage);
        this.pastQueryStorage.data = finalPastQueryItems.reduce<{ [searchTerm: string]: PastQueryItem }>((p, c) => {
            p[c.searchTerm] = c;
            return p;
        }, {});
    }

    private getQuerySuggestionedItemsFromPastQueryStorage(searchTerm: string): QuerySuggestionItem[] {
        // This method firstly finds all the past query items in the storage.
        // If a search term is given, filter all the past query items with the search term (prefix matching).
        // Then it use Frecency algoritm to sort those past query items and slice them to follow MaxPastQuerySuggestionItemCount.
        // Finally maps the past query item to query suggestion item.

        const matchedPastQueryItems = Object.keys(this.pastQueryStorage.data).reduce<PastQueryItem[]>((p, c) => {
            if (!searchTerm || c.indexOf(searchTerm) === 0) {
                p.push(this.pastQueryStorage.data[c]);
            }
            return p;
        }, []);

        return this.sortByFrecencyAlgorithm(matchedPastQueryItems)
            .slice(0, OmniboxConfigurationService.instance.configuration.pastQueryMaxCount)
            .map((item, index) => {
                const result: QuerySuggestionItem = {
                    url: this.omniboxViewModel.getUrlBySearchTerm(item.searchTerm),
                    searchTerm: item.searchTerm,
                    segments: [],
                    isPastQuery: true,
                    ownerModule: this,
                    getIdentifier: () => QuerySuggestionTestId + index.toString()
                };

                if (searchTerm) {
                    result.segments.push({ text: item.searchTerm.substring(0, searchTerm.length), keywordToAppend: false });
                    result.segments.push({ text: item.searchTerm.substring(searchTerm.length, item.searchTerm.length), keywordToAppend: true });
                } else {
                    result.segments.push({ text: item.searchTerm, keywordToAppend: false });
                }

                return result;
            });
    }

    private sortByFrecencyAlgorithm(items: PastQueryItem[]) {
        // Here is the implementation of the Frecency algorithm.
        // It firstly calculates the frecency score (considering both frequency and recency) for each item.
        // Then sort those items by their scores in desc order.
        // The implementation of this algorithm is majorly based on this document: https://slack.engineering/a-faster-smarter-quick-switcher
        items.forEach(item => {
            this.calculatePastQueryItemScore(item);
        });
        return items.sort((a, b) => this.comparePastQueryItemByScore(a, b));
    }

    private comparePastQueryItemByScore(a: PastQueryItem, b: PastQueryItem): number {
        if (a.score > b.score) return -1;
        if (a.score < b.score) return 1;
        return -(a.timestamps[a.timestamps.length - 1] - b.timestamps[b.timestamps.length - 1]); // Using negative operator in the beining to flip the sorting order to "desc" based on timestamps.
    }

    private calculatePastQueryItemScore(pastQueryItem: PastQueryItem) {
        // Firstly it goes through all its timestamps and figure out a recent score for each timestamps.
        const now = this.getNowTicks();

        const recentScore = pastQueryItem.timestamps.reduce<number>((previousScore, timestamp) => {
            const timeSpan = now - timestamp;

            for (let i = 0; i < recentScoreHandlers.length; i++) {
                const currentScore = recentScoreHandlers[i](timeSpan);
                if (currentScore) {
                    previousScore += currentScore;
                    break;
                }
            }

            return previousScore;
        }, 0);

        // Then uses the following express to calculate the total score.
        // The goal of the following calculation is to make sure the total score is decided by both the frequency and recency.
        // This expression is based on "Final score = Total Count * Score / Number of Timestamps recorded" in this document:  https://slack.engineering/a-faster-smarter-quick-switcher
        const totalScore = pastQueryItem.hitCount * recentScore / pastQueryItem.timestamps.length;
        pastQueryItem.score = totalScore;
    }

    private getLocalStorageKey() {
        return `${LocalStoraygeKeyPrefix}-${this.server.sessionUserId()}`;
    }
}
